import * as React from 'react';
import { useState } from 'react';

import { TextInput } from '@coursera/coursera-ui';

import type {
  CREATE_PASSWORD_FORM,
  RESET_PASSWORD_FORM,
} from 'bundles/authentication/components/reset-password/SetPasswordForm';
import TrackedButton from 'bundles/page/components/TrackedButton';
import { USER_EMAIL_REGEX_PATTERN } from 'bundles/user-account/common/constants';

import _t from 'i18n!nls/authentication';

type FormType = typeof RESET_PASSWORD_FORM | typeof CREATE_PASSWORD_FORM;
type Props = {
  onSubmit: (email: string) => Promise<void>;
  userEmail?: string;
  formType?: FormType;
};

const emailRegex = new RegExp(USER_EMAIL_REGEX_PATTERN, 'i');

const ResetForm = ({ onSubmit, userEmail, formType }: Props) => {
  const [email, setEmail] = useState(userEmail || '');
  const [emailInputError, setEmailInputError] = useState('');

  const isValidEmail = () => {
    if (!emailRegex.test(email)) {
      setEmailInputError(_t('This is not a valid email address. Please try again.'));
      return false;
    }
    setEmailInputError('');
    return true;
  };

  const handleEmailInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);

    if (emailInputError) {
      // if error is already being displayed, continuously try and clear it
      isValidEmail();
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidEmail()) {
      await onSubmit(email);
    }
  };

  const isCreateForm = formType === 'create';

  return (
    <form onSubmit={handleEmailSubmit}>
      <div className="auth-input-container">
        <p className="auth-page-message">
          {isCreateForm
            ? _t("Enter your email address below. We'll look for your account and send you a create password email.")
            : _t("Enter your email address below. We'll look for your account and send you a password reset email.")}
        </p>
        <TextInput
          label={_t('Email')}
          componentId="email"
          placeholder={_t('Enter email')}
          value={email}
          name="email"
          nativeHtmlAttributes={{ 'data-e2e': isCreateForm ? 'create-email-input' : 'reset-email-input' }}
          onChange={handleEmailInputChange}
          onBlur={isValidEmail}
          disabled={!!userEmail}
        />
        {emailInputError && <span className="auth-form-input-error">{emailInputError}</span>}
      </div>
      <TrackedButton
        className="primary auth-form-button"
        trackingName={isCreateForm ? 'create_page_button' : 'reset_page_button'}
        data-e2e={isCreateForm ? 'create-page-button' : 'reset-page-button'}
        type="submit"
      >
        {isCreateForm ? _t('Send Create Password') : _t('Send Password Reset')}
      </TrackedButton>
    </form>
  );
};

export default ResetForm;
